<!-- client/src/views/AquariumDetail.vue -->
<template>
  <v-container>
    <v-row>
      <v-col>
        <div @mouseenter="isHoveringName = true" @mouseleave="isHoveringName = false">
          <h1 class="mb-0">
            {{ aquarium.name }}
            <v-icon v-if="isHoveringName" class="edit-name-icon" @click="editName">mdi-pencil</v-icon>
          </h1>
        </div>
        <p>
          {{ aquarium.description }}
        </p>
        
        <a href="#" class="public-link" @click.prevent="openPublicLinkModal">Create public link</a>
        <PremiumFeatureIcon message="Creating a public link is a premium feature you have access to now, but in the future it may require a premium account." />
        <br/>
        <v-tooltip
          v-if="showAddLogTooltip"
          open-on-hover
          location="bottom"
        >
          <template #activator="{ props }">
            <v-btn 
              v-bind="props"
              color="primary" 
              class="mt-4" 
              @click="this.modalStore.openDataLogModal({
                mode: 'user',
                verb: 'create',
                prefilledData: {
                  aquarium_id: this.aquarium.id,
                  timestamp: this.formatTimestamp(new Date().toISOString()),
                },
              })">
              Create New Log
            </v-btn>
          </template>
          <span>
            Click here to add your first data log!
          </span>
        </v-tooltip>
        <p v-if="showAddLogTooltip">
          <v-icon color="primary" class="mr-1">mdi-arrow-up</v-icon>
          Use <strong>Create New Log</strong> to add your first data log!
        </p>
        <v-btn 
          v-else
          color="primary" 
          class="mt-4" 
          @click="this.modalStore.openDataLogModal({
            mode: 'user',
            verb: 'create',
            prefilledData: {
              aquarium_id: this.aquarium.id,
              timestamp: this.formatTimestamp(new Date().toISOString()),
            },
          })">
          Create New Log
        </v-btn>
        <input
          ref="csvInput"
          type="file"
          accept=".csv,text/csv"
          style="display: none"
          @change="handleCsvUpload"
        />
      </v-col>

      <v-col>
        <div class="cover-image-container">
          <v-img :src="aquarium.image_url" class="cover-image" />
          <QrCode v-if="aquarium.id !== null" :id="aquarium.id" />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12" lg="12">
        <template v-if="filteredLogs.length">
          <Chart :logs="filteredLogs" />
        </template>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3>Image Gallery</h3>
        <v-row>
          <v-col 
            v-for="image in aquarium.images" 
            :key="image.id" 
            cols="6" 
            md="4" 
            class="gallery-item"
          >
            <GalleryImage 
              :image="image"
              :aquariumId="aquarium.id"
            />
          </v-col>
        </v-row>
          <div 
            class="image-upload-area" 
            @dragover.prevent 
            @drop.prevent="handleImageDrop"
          >
            <p>Drag and drop an image here, or</p>
            <v-btn color="primary" @click="triggerFileInput">Upload Image</v-btn>
            <input 
              type="file" 
              ref="fileInput" 
              style="display: none;" 
              @change="handleImageUpload" 
            />
          </div>
      </v-col>
      <v-col>
        <ChatArea @click="openAiInfoModal" style="cursor:pointer;" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="aquarium.logs"
          :items-per-page="30"
          :sort-by="[{ key: 'timestamp', order: 'desc' }]"
          class="elevation-1"
        >
          <template v-slot:item.timestamp="{ item }">
            {{ formatTimestamp(item.timestamp) }}
          </template>
          <template v-slot:item.ammonia="{ item }">
            {{ item.ammonia ?? '' }}
          </template>
          <template v-slot:item.nitrite="{ item }">
            {{ item.nitrite ?? '' }}
          </template>
          <template v-slot:item.nitrate="{ item }">
            {{ item.nitrate ?? '' }}
          </template>
          <template v-slot:item.phosphase="{ item }">
            {{ item.phosphase ?? '' }}
          </template>
          <template v-slot:item.calcium="{ item }">
            {{ item.calcium ?? '' }}
          </template>
          <template v-slot:item.alkalinity="{ item }">
            {{ item.alkalinity ?? '' }}
          </template>
          <template v-slot:item.magnesium="{ item }">
            {{ item.magnesium ?? '' }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip text="Edit Log">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  color="primary"
                  class="mr-2"
                  size="small"
                  style="cursor:pointer"
                  @click="this.modalStore.openDataLogModal({ 
                    mode: 'user', 
                    verb: 'edit', 
                    prefilledData: { 
                      ...item, 
                      aquarium_id: aquarium.id 
                    } 
                  })"
                >mdi-pencil</v-icon>
              </template>
            </v-tooltip>
            <v-tooltip text="Delete Log">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  color="error"
                  size="small"
                  style="cursor:pointer"
                  @click="this.modalStore.openDataLogModal({ 
                    mode: 'user', 
                    verb: 'delete', 
                    prefilledData: { 
                      ...item, 
                      aquarium_id: aquarium.id 
                    } 
                  })"
                >mdi-delete</v-icon>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="d-flex align-center mb-2">
          <v-btn
            small
            color="primary"
            variant="outlined"
            @click="showAdvancedColumns = !showAdvancedColumns"
          >
            {{ showAdvancedColumns ? 'Hide Advanced Columns' : 'Show Advanced Columns' }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!--<v-row>
      <v-col>
        <h3>Calendar</h3>
        <p>to do</p>
      </v-col>
    </v-row>-->

    <v-row>
      <v-col>
        <v-btn class="mt-4 ml-2" color="red" @click="confirmDelete">Delete Aquarium</v-btn>
      </v-col>
      <v-col>
        <v-btn
          color="secondary"
          class="mt-4 ml-2"
          @click="downloadCsv"
        >
          Export Logs
        </v-btn>
      </v-col>
      <v-col class="mt-4 ml-2">
        <div class="d-flex align-center">
          <v-btn
            color="secondary"
            @click="triggerCsvInput"
          >
            Import Logs
          </v-btn>
          <v-icon
            x-small
            class="ml-1"
            color="grey"
            @click="showImportInfoModal = true"
            style="cursor: pointer;"
          >
            mdi-information
          </v-icon>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="showDeleteModal" max-width="500px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          <p>Type the name of the aquarium to confirm deletion:</p>
          <v-text-field v-model="deleteConfirmationName" label="Aquarium Name" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="showDeleteModal = false">Cancel</v-btn>
          <v-btn color="red darken-1" text @click="deleteAquarium">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteImageModalVisible" max-width="500px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete this image?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="isDeleteImageModalVisible = false">Cancel</v-btn>
          <v-btn color="red darken-1" text @click="deleteImage">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPublicLinkModal" max-width="500px">
      <v-card>
        <v-card-title>Public Link Feature - Coming Soon</v-card-title>
        <v-card-text>
          <p>
            This feature allows you to share a specific time range of data logs on a public, read-only link. 
            Share your aquarium's data on fish forums to get public input and help troubleshoot or diagnose issues with your tank.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showPublicLinkModal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAiInfoModal" max-width="500px">
      <v-card>
        <v-card-title>Ask AI Feature - Coming Soon</v-card-title>
        <v-card-text>
          <p>
            This feature shares your recorded data logs and uploaded images with an AI agent who is an expert reef-keeper. 
            You can ask any questions for advice on your tank, such as:<br>
            <em>"What fish would help clean algae and are compatible with my current livestock?"</em><br>
            <em>"How can I get rid of cyanobacteria?"</em>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showAiInfoModal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showImportInfoModal" max-width="500px">
      <v-card>
        <v-card-title>Import CSV Feature</v-card-title>
        <v-card-text>
          <p>
            This feature allows you to import data logs from a CSV file. 
            You can download a sample CSV from the <router-link to="/demo-aquarium">demo aquarium</router-link> on the landing page to see the required formatting.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showImportInfoModal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import axios from 'axios';
  import Chart from '../components/Chart.vue';
  import { usePopupStore } from "@/store";
  import { useModalStore } from "@/store";
  import PremiumFeatureIcon from '@/components/PremiumFeatureIcon.vue';
  import GalleryImage from '@/components/GalleryImage.vue';
  import ChatArea from '@/components/ChatArea.vue';
  import QrCode from '@/components/QrCode.vue';
  import { useHead } from '@vueuse/head';

  export default {
    name: 'Aquarium Detail',
    components: {
      Chart,
      PremiumFeatureIcon,
      GalleryImage,
      ChatArea,
      QrCode,
    },
    props: {
      demoAquarium: {
        type: Object,
        default: null,
      },
    },
    setup() {
      useHead({
        title: 'Reeflendar.com - Aquarium Details',
        meta: [
          { name: 'description', content: 'View and manage your aquarium details, logs, and images.' },
          { name: 'keywords', content: 'aquarium, reef tank, logs, images' },
        ],
      });
      return {
        popupStore: usePopupStore(),
        modalStore: useModalStore(),
      };
    },
    data() {
      return {
        isHoveringName: false,
        aquarium: this.demoAquarium ||{
          id: null,
          name: '',
          description: '',
          public_link: '',
          image_url: '',
          notes: '',
          images: [],
          logs: [],
        },
        chartData: [],
        chartLabels: [],
        showDeleteModal: false,
        deleteConfirmationName: '',
        isImageModalVisible: false,
        selectedImageUrl: null,
        isDeleteImageModalVisible: false,
        imageToDeleteId: null,
        showAdvancedColumns: false,
        showPublicLinkModal: false,
        showAiInfoModal: false,
        showImportInfoModal: false,
        headersBase: [
          { title: 'Timestamp', value: 'timestamp' },
          { title: 'Temperature (°F)', value: 'temperature' },
          { title: 'pH', value: 'ph' },
          { title: 'Salinity (SG)', value: 'salinity' },
          { title: 'Notes', value: 'note' },
        ],
        headersAdvanced: [
          { title: 'Ammonia (ppm)', value: 'ammonia' },
          { title: 'Nitrite (ppm)', value: 'nitrite' },
          { title: 'Nitrate (ppm)', value: 'nitrate' },
          { title: 'Phosphate (ppm)', value: 'phosphase' },
          { title: 'Calcium (ppm)', value: 'calcium' },
          { title: 'Alkalinity (dKH)', value: 'alkalinity' },
          { title: 'Magnesium (ppm)', value: 'magnesium' },
        ],
        headers: [
          { title: 'Timestamp', value: 'timestamp' },
          { title: 'Temperature (°F)', value: 'temperature' },
          { title: 'pH', value: 'ph' },
          { title: 'Salinity', value: 'salinity' },
          { title: 'Notes', value: 'note' },
          { title: 'Actions', value: 'actions', sortable: false },
        ],
      };
    },
    computed: {
      filteredLogs() {
        return this.aquarium.logs;
      },
      isDemoMode() {
        return !!this.demoAquarium;
      },
      tableHeaders() {
        return [
          ...this.headersBase,
          ...(this.showAdvancedColumns ? this.headersAdvanced : []),
          { title: 'Actions', value: 'actions', sortable: false }
        ];
      },
      showAddLogTooltip() {
        return !this.aquarium.logs || this.aquarium.logs.length === 0;
      },
    },
    watch: {
      '$route.params.id': {
        handler() {
          this.fetchAquarium();
        },
        immediate: true,
      },
    },
    methods: {
      //#region CRUD operations
      async fetchAquarium() {
        if (this.isDemoMode) return;
        const aquariumId = this.$route.params.id;
        if (!aquariumId) {
          this.popupStore.setMessage('Error fetching aquarium', error.message);
          return;
        }
        try {
          const response = await axios.get(`/api/aquariums/${aquariumId}`);
          this.aquarium = response.data;
        } catch (error) {
          if (error.response && error.response.status === 404) {
            this.popupStore.setMessage('Aquarium not found.', 'The requested aquarium could not be found.');
            this.$router.push({ name: 'Dashboard' });
          } else {
            this.popupStore.setMessage('Error fetching aquarium', error.message);
          }
        }
      },
      editName() {
        this.modalStore.openNewAquariumModal({
          mode: 'user',
          verb: 'edit',
          prefilledData: {
            id: this.aquarium.id,
            name: this.aquarium.name,
            description: this.aquarium.description,
          },
        });
      },
      confirmDelete() {
        this.showDeleteModal = true;
      },
      async deleteAquarium() {
        if (this.deleteConfirmationName !== this.aquarium.name) {
          this.popupStore.setMessage('Aquarium name does not match.', 'Please enter the correct aquarium name.');
          return;
        }
        try {
          await axios.delete(`/api/aquariums/${this.aquarium.id}`);
          this.$router.push({ name: 'Dashboard' });
        } catch (error) {
          this.popupStore.setMessage('Error deleting aquarium', error.message);
        }
      },
      //#endregion

      //#region Timestamp formatting
      formatTimestamp(ts) {
        if (!ts) return '';
        const date = new Date(ts);
        if (isNaN(date)) return ts;
        const pad = n => n.toString().padStart(2, '0');
        let hours = date.getHours();
        const minutes = pad(date.getMinutes());
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        return `${year}-${month}-${day} ${pad(hours)}:${minutes} ${ampm}`;
      },
      //#endregion

      //#region Image Management functions
      addNewImage(newImage) {
        this.fetchAquarium();
        this.popupStore.setMessage('Success', 'Image uploaded successfully', 'success');
      },
      async handleImageUpload(event) {
        const file = event.target.files[0];
        if (!file) return;
        await this.uploadImage(file);
      },
      async handleImageDrop(event) {
        const file = event.dataTransfer.files[0];
        if (!file) return;
        await this.uploadImage(file);
      },
      async uploadImage(file) {
        if (this.isDemoMode) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('aquarium_id', this.aquarium.id);
        
        try {
          const response = await axios.post('/api/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          this.addNewImage(response.data);
        } catch (error) {
          this.popupStore.setMessage('Error uploading image', error.message);
        }
      },
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      //#endregion

      //#region Import/export functions
      downloadCsv() {
        if (!this.aquarium.logs || !this.aquarium.logs.length) {
          this.popupStore.setMessage('No logs to export', 'There are no logs to download.');
          return;
        }
        // Combine base and advanced headers for export
        const allHeaders = [
          { label: 'Timestamp', key: 'timestamp' },
          { label: 'Temperature (°F)', key: 'temperature' },
          { label: 'pH', key: 'ph' },
          { label: 'Salinity (SG)', key: 'salinity' },
          { label: 'Ammonia (ppm)', key: 'ammonia' },
          { label: 'Nitrite (ppm)', key: 'nitrite' },
          { label: 'Nitrate (ppm)', key: 'nitrate' },
          { label: 'Phosphate (ppm)', key: 'phosphase' },
          { label: 'Calcium (ppm)', key: 'calcium' },
          { label: 'Alkalinity (dKH)', key: 'alkalinity' },
          { label: 'Magnesium (ppm)', key: 'magnesium' },
          { label: 'Notes', key: 'note' },
        ];
        const csvRows = [];
        csvRows.push(allHeaders.map(h => `"${h.label}"`).join(','));    // Add header row
        this.aquarium.logs.forEach(log => {     // Add data rows
          csvRows.push(allHeaders.map(h => {
            let val = log[h.key];
            if (h.key === 'timestamp') val = this.formatTimestamp(val);
            if (val === undefined || val === null) val = '';
            return `"${String(val).replace(/"/g, '""')}"`;
          }).join(','));
        });
        const csvContent = csvRows.join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.aquarium.name || 'aquarium'}-logs.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      triggerCsvInput() {
        this.$refs.csvInput.value = null;
        this.$refs.csvInput.click();
      },
      async handleCsvUpload(event) {
        const file = event.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
          const response = await axios.post(
            `/api/logs/upload_csv?aquarium_id=${this.aquarium.id}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          const data = response.data;
          let msg = `Created: ${data.created}, Updated: ${data.updated}, Rejected: ${data.rejected}`;
          if (data.errors && data.errors.length) {
            msg += `<br><br>${data.errors.length} errors:<br><br>` +
              data.errors.map(e =>
                `Line ${e.line}: ${e.message}${e.row ? `<br>Row: ${e.row.join(', ')}` : ''}`
              ).join('<br><br>');
            this.popupStore.setMessage('CSV Import Result', msg, 'warning');
          } else {
            this.popupStore.setMessage('CSV Import Result', msg, 'success');
          }
          this.fetchAquarium();
        } catch (error) {
          let msg = 'CSV upload failed.';
          if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message;
            if (error.response.data.errors) {
              msg += '<br><br>' + error.response.data.errors.map(e =>
                `Line ${e.line}: ${e.message}${e.row ? `<br>Row: ${e.row.join(', ')}` : ''}`
              ).join('<br>');
            }
          }
          this.popupStore.setMessage('CSV Import Error', msg, 'error');
        }
        this.fetchAquarium();
      },
      //#endregion

      //#region Public Link
      openPublicLinkModal() {
        this.showPublicLinkModal = true;
      },
      //#endregion

      //#region AI Chatbot
      openAiInfoModal() {
        this.showAiInfoModal = true;
      },
      //#endregion
    },
    mounted() {
      if (!this.isDemoMode) {
        this.fetchAquarium();
        this.$eventBus.on('aquarium-updated', this.fetchAquarium);
        this.$eventBus.on('log-updated', this.fetchAquarium);
        this.$eventBus.on('image-deleted', this.fetchAquarium);
        this.$eventBus.on('cover-image-set', this.fetchAquarium);
      }
    },
    beforeUnmount() {
      if (!this.isDemoMode) {
        this.$eventBus.off('aquarium-updated', this.fetchAquarium);
        this.$eventBus.off('log-updated', this.fetchAquarium);
        this.$eventBus.off('image-deleted', this.fetchAquarium);
        this.$eventBus.off('cover-image-set', this.fetchAquarium);
      }
    },
  };
</script>

<style scoped>
  .edit-name-icon {
    cursor: pointer;
    font-size: 0.6em;
  }

  .image-upload-area {
    border: 2px dashed rgb(var(--v-theme-border));
    padding: 20px;
    text-align: center;
    margin-top: 20px;
    cursor: pointer;
    border-radius: 8px;
    background-color: rgb(var(--v-theme-surface));
  }

  .image-upload-area:hover {
    background-color: rgba(var(--v-theme-lightSurface));
  }

  .cover-image-container {
    width: 180px;
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
</style>